import { Dispatch, SetStateAction, Suspense, useEffect, useRef, useState } from 'react';
import { DataTable, DataTableStateEvent } from 'primereact/datatable';
import { Column, ColumnFilterElementTemplateOptions } from 'primereact/column';
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect';
import { useAppSelector } from '../../redux/hooks';
import { selectUser } from '../../redux/reducers/userSlice';
import { QueryTeachersData } from './QueryTeachersData';
import { useLazyLoadQuery } from 'react-relay';
import { getTeachersTableFiltersInfo } from '../../graphQLOperations/TeachersDataTable';
import { TeachersDataTableFiltersQuery } from '../../graphQLOperations/__generated__/TeachersDataTableFiltersQuery.graphql';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { CompleteUser } from '../../custom-hooks/useGetUsersBySite';
import { Roles } from '../../models/User';
import { Tooltip } from 'primereact/tooltip';
import { useDebounce } from 'primereact/hooks';
import { PageInfo } from '../../models/PageInfo';
import { useHiddenClassIds } from '../../custom-hooks/useHiddenClassIds';
import { SquarePen } from 'lucide-react';

export interface TeacherTableFilterState {
  first: number;
  rows: number;
  page?: number;
  pageCount: number;
  sortField: string;
  sortOrder: 1 | -1;
  totalPages: number;
  filters: Filters;
  multiSortMeta: null;
  prevPage: number;
}

interface Filters {
  name: string;
  emailAddress: string;
  sites: { id: string; name: string }[] | null;
  roles: { id: string; name: string }[] | null;
  classes: { id: string; name: string }[] | null;
  [key: string]: string | { id: string; name: string }[] | null;
}

const filterStateInitValue: TeacherTableFilterState = {
  first: 0,
  rows: 5,
  page: 0,
  pageCount: 0,
  sortField: 'name',
  sortOrder: 1,
  totalPages: 1,
  filters: {
    name: '',
    emailAddress: '',
    sites: [],
    roles: null,
    classes: null,
  },
  multiSortMeta: null,
  prevPage: 0,
};

interface Props {
  refetchTeachertKey?: number;
  setOpenEnrollTeacher: Dispatch<SetStateAction<boolean>>;
  setSelectedTeacher: Dispatch<SetStateAction<CompleteUser | undefined>>;
}

const TeachersDataTable = ({ setOpenEnrollTeacher, setSelectedTeacher, refetchTeachertKey }: Props) => {
  const userRedux = useAppSelector(selectUser);
  const [usersList, setUserslist] = useState<CompleteUser[]>([]);
  const [firstLoad, setFirstLoad] = useState<boolean>(true);
  const [fetchingData, setFetchingData] = useState<boolean>(true);
  const [nameAndEmailValue, debouncedNameAndEmail, setNameAndEmail] = useDebounce({ name: '', email: '' }, 500);
  const [totalRecords, setTotalRecords] = useState<number>(0);
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);
  const [teacherPageCursors, setTeacherPageCursors] = useState<
    Record<number, { endCursor?: string | null; startCursor?: string | null }>
  >({});

  useEffect(() => {
    if (!firstLoad) setFetchingData(true);
  }, [refetchTeachertKey]);

  useEffect(() => {
    setFetchingData(true);
    setFilterState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        name: debouncedNameAndEmail.name,
        emailAddress: debouncedNameAndEmail.email,
      },
    }));
  }, [debouncedNameAndEmail]);

  const { allRoles, classes } = useLazyLoadQuery<TeachersDataTableFiltersQuery>(
    getTeachersTableFiltersInfo,
    {},
    { fetchPolicy: 'network-only' },
  );
  const hiddenClassIds = useHiddenClassIds();
  const classFilterOptions = (classes?.nodes ?? []).filter(cls => cls && !hiddenClassIds.has(cls.id));
  const [filterState, setFilterState] = useState<TeacherTableFilterState>({
    ...filterStateInitValue,
    filters: {
      ...filterStateInitValue.filters,
      sites: userRedux?.sites?.map(st => ({ id: st.id || '', name: st.name || '' })) || [],
    },
  });

  const didInitRolesRef = useRef(false);
  useEffect(() => {
    if (didInitRolesRef.current) return;

    const roleNodes = allRoles?.nodes;
    if (!roleNodes || roleNodes.length === 0) return;

    setFilterState(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        roles: roleNodes
          .filter((rl): rl is { id: string; name: string } => !!rl?.id && !!rl?.name && rl.id !== 'RoleNode:2')
          .map(rl => ({
            id: rl.id,
            name: rl.name,
          })),
      },
    }));

    didInitRolesRef.current = true;
  }, [allRoles]);
  const rowFilterTemplate = (option: ColumnFilterElementTemplateOptions, options: { id: string; name: string }[]) => (
    <MultiSelect
      disabled={!options.length}
      name={option.field}
      optionLabel="name"
      value={(filterState.filters as Filters)[option.field]}
      options={options}
      itemTemplate={option => {
        return <span key={option.id}>{option?.section ? `${option.name} - ${option.section}` : option?.name}</span>;
      }}
      onChange={(e: MultiSelectChangeEvent) => {
        setFetchingData(true);
        setFilterState(prev => ({
          ...prev,
          filters: {
            ...prev.filters,
            [e.target.name]:
              e.target.name === 'sites' || e.target.name === 'roles'
                ? e.target.value
                : e.target.value.length
                ? e.target.value
                : null,
          },
        }));
      }}
      placeholder="Search"
      className="p-column-filter"
      maxSelectedLabels={1}
      style={{ minWidth: '14rem' }}
    />
  );

  const isEditableRow = (rowData: CompleteUser) => {
    if (rowData.externalSync) return false;

    if (rowData?.id === `UserNode:${userRedux?.id}`) return true;

    if (userRedux?.roles?.some(role => role?.name === Roles.NETWORK_ADMIN)) {
      if (
        rowData.role.some(
          r => r.name !== Roles.NETWORK_ADMIN && (r.name === Roles.SITE_ADMIN || r.name === Roles.TEACHER),
        )
      )
        return true;
    }

    if (userRedux?.roles?.some(role => role?.name === Roles.SITE_ADMIN)) {
      if (
        rowData.role.some(
          r => r.name !== Roles.NETWORK_ADMIN && r.name !== Roles.SITE_ADMIN && r.name === Roles.TEACHER,
        )
      )
        return true;
    }

    return false;
  };

  return (
    <>
      <Suspense>
        <QueryTeachersData
          filterState={filterState}
          setFetchingData={setFetchingData}
          fetchingData={fetchingData}
          setTotalRecords={setTotalRecords}
          setFirstLoad={setFirstLoad}
          setPageInfo={setPageInfo}
          pageInfo={pageInfo}
          firstLoad={firstLoad}
          setUsersFiltered={setUserslist}
          teacherPageCursors={teacherPageCursors}
          setTeacherPageCursors={setTeacherPageCursors}
        />
      </Suspense>
      <DataTable
        lazy
        loading={fetchingData}
        stripedRows
        paginator
        rows={filterState.rows}
        rowsPerPageOptions={[5, 10, 25]}
        sortField="name"
        totalRecords={totalRecords}
        sortOrder={filterState.sortOrder}
        filterDisplay="row"
        emptyMessage="No teachers or admins found"
        value={usersList}
        scrollable
        first={filterState.first}
        scrollHeight="600px"
        className="w-full"
        onPage={(e: DataTableStateEvent) => {
          setFetchingData(true);
          const newPage: TeacherTableFilterState = {
            ...filterState,
            page: e.page,
            rows: e.rows,
            first: e.first,
            pageCount: e.pageCount!,
            prevPage: filterState.page!,
          };
          setFilterState(newPage);
        }}
        onSort={() => {
          setFetchingData(true);
          setFilterState({ ...filterState, sortOrder: filterState.sortOrder === -1 ? 1 : -1 });
        }}
        paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
      >
        <Column
          style={{ width: '25%' }}
          frozen
          sortable
          field="name"
          header="Name"
          filter
          filterElement={() => (
            <InputText
              placeholder="Search by name"
              value={nameAndEmailValue.name}
              onChange={e => {
                setNameAndEmail({ ...nameAndEmailValue, name: e.target.value });
              }}
            />
          )}
        />
        <Column
          field="emailAddress"
          header="Email"
          filter
          filterPlaceholder="Search by email"
          filterElement={() => (
            <InputText
              placeholder="Search by email"
              value={nameAndEmailValue.email}
              onChange={e => {
                setNameAndEmail({ ...nameAndEmailValue, email: e.target.value });
              }}
            />
          )}
        />
        <Column
          field="sites"
          header="Site"
          filter
          filterElement={option =>
            rowFilterTemplate(option, userRedux?.sites?.map(st => ({ id: st.id || '', name: st.name || '' })) || [])
          }
          body={element => <span>{element.site?.map((st: { name: string }) => st.name).join(', ')}</span>}
          showFilterMenu={false}
        />
        <Column
          field="roles"
          header="User role"
          filter
          filterElement={option =>
            rowFilterTemplate(
              option,
              allRoles?.nodes?.filter(rl => rl.id !== 'RoleNode:2' && rl.id !== 'RoleNode:1') as {
                id: string;
                name: string;
              }[],
            )
          }
          showFilterMenu={false}
          body={element => <span>{element.role?.map((rl: { name: string; id: string }) => rl.name).join(', ')}</span>}
        />
        <Column
          field="classes"
          filter
          filterElement={option =>
            rowFilterTemplate(option, classFilterOptions as { id: string; name: string; section: string }[])
          }
          header="Class(es)"
          showFilterMenu={false}
        />
        <Column
          frozen
          alignFrozen="right"
          rowEditor={true}
          headerStyle={{ width: '5%', minWidth: '4rem' }}
          bodyStyle={{ textAlign: 'center' }}
          body={(rowData: CompleteUser) =>
            isEditableRow(rowData) ? (
              <Button
                icon={<SquarePen size={18} aria-hidden style={{ flexShrink: 0 }} />}
                aria-label="Edit the user"
                tooltip="Edit the user"
                rounded
                outlined
                onClick={() => {
                  setOpenEnrollTeacher(true);
                  setSelectedTeacher(rowData);
                }}
              />
            ) : rowData.externalSync ? (
              <>
                <Tooltip target={'.nc_icon-nc_info'}>
                  This user is managed by Clever Sync. Any changes in Clever will be updated each night into Roadmaps.
                </Tooltip>
                <i className="nc_icon-nc_info icon-md" data-pr-position="left" />
              </>
            ) : null
          }
        />
      </DataTable>
    </>
  );
};

export default TeachersDataTable;
